import { Link } from 'react-router-dom';
import { PlaneLanding } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useArrivalInformation } from '../hooks/useArrivalInformation';

/**
 * Dashboard card for the post-placement arrival information screen (Task #1673).
 *
 * Behaviour (purely additive surface, same contract as its cardgrid
 * neighbour RenewalEligibilityCard):
 * - While loading, on error, or when `available: false`
 *   (NO_CONFIRMED_PLACEMENT) it renders NOTHING — the card is simply absent
 *   from the dashboard until a placement is confirmed. No skeleton: a
 *   placeholder that appears and then vanishes for every pre-placement
 *   applicant would shift the grid for the majority who have no placement.
 * - `available: true`: renders a summary card with key placement details and
 *   a "View details" link to /arrival-information.
 *
 * The shared useArrivalInformation() hook supplies the query options — a
 * single queryKey and staleTime for this card and the detail page, so
 * navigating to the page reuses the cached payload instead of refetching.
 */
export function ArrivalInformationCard() {
  const { data, isLoading, isError } = useQuery(useArrivalInformation());

  if (isLoading || isError || !data || !data.available) return null;

  return (
    <div className="card actioncard" data-testid="arrival-information-card">
      <div className="actioncard-head">
        <span className="actioncard-icon" aria-hidden><PlaneLanding size={20} /></span>
        <h2 style={{ margin: 0 }}>Arrival information</h2>
        <span className="pill ok">Placement confirmed</span>
      </div>
      {data.hostName && (
        <p style={{ margin: 0, color: 'var(--ink-2)' }}>
          Your confirmed placement details for <strong>{data.hostName}</strong>.
        </p>
      )}
      <ul className="feed">
        {data.returnToUponArrival && (
          <li><strong>Report to on arrival:</strong> {data.returnToUponArrival}</li>
        )}
        {data.contactName && (
          <li><strong>Contact:</strong> {data.contactName}</li>
        )}
        {data.pickUpProvided && (
          <li>
            <strong>Transport pick-up:</strong>{' '}
            {data.transportPickUpCost ?? 'Provided'}
          </li>
        )}
        {data.nearestAirportToJobsite && (
          <li><strong>Nearest airport:</strong> {data.nearestAirportToJobsite}</li>
        )}
      </ul>
      <Link to="/arrival-information" className="btn-outline" data-testid="arrival-information-details">
        View details
      </Link>
    </div>
  );
}
